import { useEffect, useRef } from 'react'
import {
  createChart,
  createSeriesMarkers,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
  type IChartApi,
  type CandlestickData,
  type LineData,
  type HistogramData,
  type Time,
  ColorType,
  LineStyle,
  CrosshairMode,
} from 'lightweight-charts'
import type { DailyScore, BacktestTrade } from '../lib/supabase'

interface Props {
  scores: DailyScore[]
  trades: BacktestTrade[]
  outcomeFilter: 'all' | 'wins' | 'losses'
  signalFilter: 'all' | 'entry' | 'strong'
  dateRange: { start: string; end: string }
}

function toTime(dateStr: string): Time {
  const [y, m, d] = dateStr.split('-').map(Number)
  return { year: y, month: m, day: d } as Time
}

export function StrategyChart({ scores, trades, outcomeFilter, signalFilter, dateRange }: Props) {
  const mainRef = useRef<HTMLDivElement>(null)
  const tqRef = useRef<HTMLDivElement>(null)
  const rsiRef = useRef<HTMLDivElement>(null)
  const volRef = useRef<HTMLDivElement>(null)

  const chartsRef = useRef<IChartApi[]>([])

  // Build base data arrays
  const candleData: CandlestickData[] = scores.map(s => ({
    time: toTime(s.date),
    open: s.close - (Math.random() - 0.5) * 2,
    high: s.close + Math.random() * 2,
    low: s.close - Math.random() * 2,
    close: s.close,
  }))

  const sma50Data: LineData[] = scores
    .filter(s => s.sma_50 !== null)
    .map(s => ({ time: toTime(s.date), value: s.sma_50! }))

  const sma200Data: LineData[] = scores
    .filter(s => s.sma_200 !== null)
    .map(s => ({ time: toTime(s.date), value: s.sma_200! }))

  const tqData: LineData[] = scores.map(s => ({
    time: toTime(s.date),
    value: s.score,
  }))

  const rsiData: LineData[] = scores
    .filter(s => s.rsi !== null)
    .map(s => ({ time: toTime(s.date), value: s.rsi! }))

  const volData: HistogramData[] = scores.map((s, i) => ({
    time: toTime(s.date),
    value: s.volume,
    color: s.close >= (scores[i - 1]?.close ?? s.close)
      ? 'rgba(0, 200, 83, 0.5)'
      : 'rgba(255, 23, 68, 0.5)',
  }))

  // Filter trades
  const filteredTrades = trades.filter(t => {
    if (outcomeFilter === 'wins' && t.outcome !== 'win') return false
    if (outcomeFilter === 'losses' && t.outcome !== 'loss') return false
    if (signalFilter === 'entry' && t.entry_score < 65) return false
    if (signalFilter === 'strong' && t.entry_score < 80) return false
    return true
  })

  useEffect(() => {
    // Cleanup previous charts
    chartsRef.current.forEach(c => c.remove())
    chartsRef.current = []

    if (!mainRef.current || !tqRef.current || !rsiRef.current || !volRef.current) return
    if (scores.length === 0) return

    const chartOpts = {
      layout: {
        background: { type: ColorType.Solid as const, color: 'transparent' },
        textColor: '#9aa0a6',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: 'rgba(37, 42, 53, 0.5)' },
        horzLines: { color: 'rgba(37, 42, 53, 0.5)' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#252a35' },
      timeScale: { borderColor: '#252a35', timeVisible: false },
    }

    // --- Main Chart (Candlestick + SMA overlays) ---
    const mainChart = createChart(mainRef.current, {
      ...chartOpts,
      height: 300,
      timeScale: { ...chartOpts.timeScale, visible: false },
    })

    const candleSeries = mainChart.addSeries(CandlestickSeries, {
      upColor: '#00c853',
      downColor: '#ff1744',
      borderDownColor: '#ff1744',
      borderUpColor: '#00c853',
      wickDownColor: '#ff1744',
      wickUpColor: '#00c853',
    })
    candleSeries.setData(candleData)

    const sma50Series = mainChart.addSeries(LineSeries, {
      color: '#2196f3',
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      priceLineVisible: false,
      lastValueVisible: false,
    })
    sma50Series.setData(sma50Data)

    const sma200Series = mainChart.addSeries(LineSeries, {
      color: '#ff5722',
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      priceLineVisible: false,
      lastValueVisible: false,
    })
    sma200Series.setData(sma200Data)

    // Trade markers on candle chart
    const markers = filteredTrades.flatMap(t => [
      {
        time: toTime(t.entry_date),
        position: 'belowBar' as const,
        color: '#00d4ff',
        shape: 'arrowUp' as const,
        text: `B ${t.entry_score}`,
      },
      {
        time: toTime(t.exit_date),
        position: 'aboveBar' as const,
        color: t.outcome === 'win' ? '#00c853' : '#ff1744',
        shape: 'arrowDown' as const,
        text: `S ${t.pnl_pct > 0 ? '+' : ''}${t.pnl_pct}%`,
      },
    ]).sort((a, b) => {
      const aT = a.time as { year: number; month: number; day: number }
      const bT = b.time as { year: number; month: number; day: number }
      return aT.year !== bT.year ? aT.year - bT.year : aT.month !== bT.month ? aT.month - bT.month : aT.day - bT.day
    })
    createSeriesMarkers(candleSeries, markers)

    chartsRef.current.push(mainChart)

    // --- TrendQual Score Chart ---
    const tqChart = createChart(tqRef.current, {
      ...chartOpts,
      height: 100,
      timeScale: { ...chartOpts.timeScale, visible: false },
    })

    const tqLine = tqChart.addSeries(LineSeries, {
      color: '#4caf50',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    })
    tqLine.setData(tqData)

    // Threshold lines
    tqLine.createPriceLine({ price: 65, color: 'rgba(0, 212, 255, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false })
    tqLine.createPriceLine({ price: 80, color: 'rgba(0, 200, 83, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false })
    tqLine.createPriceLine({ price: 40, color: 'rgba(255, 23, 68, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false })

    chartsRef.current.push(tqChart)

    // --- RSI Chart ---
    const rsiChart = createChart(rsiRef.current, {
      ...chartOpts,
      height: 80,
      timeScale: { ...chartOpts.timeScale, visible: false },
    })

    const rsiLine = rsiChart.addSeries(LineSeries, {
      color: '#ab47bc',
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: true,
    })
    rsiLine.setData(rsiData)
    rsiLine.createPriceLine({ price: 50, color: 'rgba(171, 71, 188, 0.3)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false })
    rsiLine.createPriceLine({ price: 70, color: 'rgba(171, 71, 188, 0.3)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false })

    chartsRef.current.push(rsiChart)

    // --- Volume Chart ---
    const volChart = createChart(volRef.current, {
      ...chartOpts,
      height: 60,
    })

    const volBars = volChart.addSeries(HistogramSeries, {
      priceLineVisible: false,
      lastValueVisible: false,
    })
    volBars.setData(volData)

    chartsRef.current.push(volChart)

    // --- Sync time scales ---
    const syncTimeScales = (source: IChartApi, targets: IChartApi[]) => {
      source.timeScale().subscribeVisibleLogicalRangeChange(range => {
        if (range) {
          targets.forEach(t => t.timeScale().setVisibleLogicalRange(range))
        }
      })
    }

    syncTimeScales(mainChart, [tqChart, rsiChart, volChart])
    syncTimeScales(tqChart, [mainChart, rsiChart, volChart])
    syncTimeScales(rsiChart, [mainChart, tqChart, volChart])
    syncTimeScales(volChart, [mainChart, tqChart, rsiChart])

    // Set visible range if dateRange filters are set
    if (dateRange.start && dateRange.end) {
      const from = toTime(dateRange.start)
      const to = toTime(dateRange.end)
      chartsRef.current.forEach(c => {
        c.timeScale().setVisibleRange({ from, to })
      })
    } else {
      chartsRef.current.forEach(c => c.timeScale().fitContent())
    }

    // Resize handler
    const handleResize = () => {
      const width = mainRef.current?.clientWidth || 600
      chartsRef.current.forEach(c => c.applyOptions({ width }))
    }

    const observer = new ResizeObserver(handleResize)
    if (mainRef.current) observer.observe(mainRef.current)

    return () => {
      observer.disconnect()
      chartsRef.current.forEach(c => c.remove())
      chartsRef.current = []
    }
  }, [scores, filteredTrades.length, dateRange.start, dateRange.end])

  if (scores.length === 0) {
    return (
      <div className="strategy-chart-container">
        <div className="empty-state">
          <div className="empty-state-text">No chart data available</div>
        </div>
      </div>
    )
  }

  return (
    <div className="strategy-chart-container">
      <div className="strategy-chart-labels">
        <span className="strategy-chart-label">
          <span style={{ color: '#2196f3' }}>SMA50</span>
          {' / '}
          <span style={{ color: '#ff5722' }}>SMA200</span>
        </span>
      </div>
      <div ref={mainRef} className="strategy-chart-pane" />
      <div className="strategy-chart-labels">
        <span className="strategy-chart-label" style={{ color: '#4caf50' }}>TrendQual Score</span>
      </div>
      <div ref={tqRef} className="strategy-chart-pane" />
      <div className="strategy-chart-labels">
        <span className="strategy-chart-label" style={{ color: '#ab47bc' }}>RSI</span>
      </div>
      <div ref={rsiRef} className="strategy-chart-pane" />
      <div className="strategy-chart-labels">
        <span className="strategy-chart-label">Volume Score</span>
      </div>
      <div ref={volRef} className="strategy-chart-pane" />
    </div>
  )
}
